import { CodePermission, CodePermissionType } from '../models/qr';

export interface CheckPermissionsParams {
  permissions?: CodePermission[];
  username: string;
  userLocation?: string;
}

/**
 * Checks if a user has permission to edit a resource based on permissions array.
 *
 * Permission logic:
 * - No permissions → anyone can edit (returns true)
 * - Has permissions → can edit if user matches any user permission OR belongs to any location permission
 *
 * @param params.permissions - The permissions array from the code
 * @param params.username - The user's email/username
 * @param params.userLocation - The user's cdsLocation (optional)
 * @returns true if allowed to edit, false otherwise
 */
export function hasEditPermission({
  permissions,
  username,
  userLocation,
}: CheckPermissionsParams): boolean {
  // If permissions is empty, anyone can edit
  if (!permissions || permissions.length === 0) {
    return true;
  }

  const locationPermissions = permissions.filter(
    (p) => p.type === CodePermissionType.Location
  );
  const userPermissions = permissions.filter(
    (p) => p.type === CodePermissionType.User
  );

  // Check if user matches any user permission
  const userMatches = userPermissions.some((p) => p.value === username);

  // Check if user belongs to any allowed location
  const userBelongsToLocation =
    userLocation && locationPermissions.some((p) => p.value === userLocation);

  // Allow if either condition is met
  return userMatches || Boolean(userBelongsToLocation);
}
